/**
 * PushHealthTable — Plan 06-04 Task 2.
 *
 * Renders one <li> per family member: avatar + short_name + status pill +
 * sub-note (active-count when devices exist; "ingen abonnement registrert"
 * otherwise). Mounted inside <AdminDashboardPanel> §Section 3.
 *
 * State-primitive contract (UI-SPEC §"Layout Shapes" 4 + 06-01):
 *   - Loading  → <LoadingState lines={4} />
 *   - Error    → <ErrorState variant="inline" onRetry=refetch />
 *   - Låst     → <AdminScopeLocked> når feilen er en manglende admin-scope
 *                (D-04) — «Prøv igjen» ville bare gitt samme 403
 *   - Empty    → <EmptyState variant="inline" /> (no admin push subs exist)
 *   - Populated → <ul aria-label> + flex rows
 *
 * Status pill styling — admin-flavoured map per UI-SPEC §Colour table
 * (D-10). #159 (steg 9c): tonene er nå ok / warn / neutral.
 * Inline span (not the shared
 * <StatusPill> primitive) because StatusPill's kinds (claimed/awaiting/
 * approved/etc.) carry their own i18n labels via chores.status.* — admin
 * push uses a separate label namespace (admin.push.status.*) so a small
 * inline pill keeps both surfaces clean.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ErrorState,
  LoadingState,
  EmptyState,
} from '../../components/primitives';
import { InitialAvatar, asColorSlot } from '../../components/primitives/InitialAvatar';
import { TONE_CLASS } from '../../components/primitives/Pill';
import type { PushHealthRow, PushHealthStatus } from '@stell/shared';
import { usePushHealth } from './usePushHealth';
import { AdminScopeLocked } from './AdminScopeLocked';
import { isAdminScopeError } from './useMembers';

interface PillTheme {
  /** Tailwind utility classes for the inline status pill. */
  surface: string;
}

const PILL: Record<PushHealthStatus, PillTheme> = {
  Aktiv: { surface: TONE_CLASS.ok },
  Utgått: { surface: TONE_CLASS.warn },
  Ukjent: { surface: TONE_CLASS.neutral },
};

function StatusBadge({ status }: { status: PushHealthStatus }): ReactElement {
  const cls = PILL[status].surface;
  return (
    <span
      data-testid="push-status-pill"
      className={[
        'inline-flex items-center rounded-pill px-2 py-0.5 text-caption font-bold',
        cls,
      ].join(' ')}
    >
      {status}
    </span>
  );
}

export function PushHealthTable(): ReactElement {
  const { t } = useTranslation();
  const query = usePushHealth();

  if (query.isLoading) {
    return <LoadingState lines={4} />;
  }

  if (query.isError && isAdminScopeError(query.error)) {
    return <AdminScopeLocked testIdPrefix="push" message={t('admin.push.scope_locked')} />;
  }

  if (query.isError) {
    return (
      <ErrorState
        variant="inline"
        heading={t('admin.push.error.heading')}
        body={t('admin.push.error.body')}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const rows: PushHealthRow[] = query.data?.rows ?? [];
  if (rows.length === 0) {
    return (
      <EmptyState
        variant="inline"
        heading={t('admin.push.empty.heading')}
        body={t('admin.push.empty.body')}
      />
    );
  }

  return (
    <ul
      aria-label={t('admin.push.section_aria')}
      className="space-y-2"
      data-testid="push-health-list"
    >
      {rows.map((row) => (
        <li
          key={row.member_id}
          aria-label={t('admin.push.row_aria', { name: row.short_name, status: row.status })}
          className="flex items-center gap-3 min-h-11"
        >
          <InitialAvatar name={row.short_name} colorSlot={asColorSlot(row.color_token)} size={32} decorative />
          <div className="flex-1">
            <div className="text-sm font-extrabold text-ink">{row.short_name}</div>
            <div className="tnum text-caption text-ink-2">
              {row.device_count > 0
                ? t('admin.push.device_count', { count: row.active_count })
                : t('admin.push.no_subscription')}
            </div>
          </div>
          <StatusBadge status={row.status} />
        </li>
      ))}
    </ul>
  );
}
